/**
 * Integrytas Group - Base App (MOBILE)
 * Page: EDS.RECIPIENT.LIST — mobile-native.
 *
 * Touch-first counterpart of the "Recipients" tab in
 * src/pages/eds/EdsRecipientPage.tsx. A DISTINCT presentation (dense card list +
 * facet chips + bottom-sheet detail + an edit sheet) over the SAME data/logic
 * layer: individual email recipients are read via apiDb (eds_recipient), and
 * every write — the quick opt-out toggle and the edit sheet's save — routes
 * through the Common Routine (routedUpdate) exactly like the desktop page (§2).
 * The desktop's list-membership / CSV-import concerns stay on the desktop; the
 * mobile surface focuses on finding a recipient and correcting their standing.
 * §16: never the desktop grid re-hosted in a phone viewport.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, Mail, MailX, MailCheck, SquarePen, X } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { routedUpdate } from '../../lib/routed_writes';
import { useFormatDate } from '../../lib/format_date';
import { localeName } from '../../i18n/locale_display';
import {
  MobilePageHeader, MobileContentCard, MobileCardRow, MobileDetailRow, MobileFilterChips, MobileStatusPill,
  type MobileLeadTone,
} from '../components';
import { MobileRelatedChildren } from '../components/MobileRelatedChildren';
import type { AuthSession } from '../../types/auth';
import type { EdsRecipientRow, YN } from '../../types/eds';
import type { MobilePageComponentProps } from '../mobile_page_components';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'EDS.RECIPIENT.LIST';

type StatusFacet = 'all' | 'active' | 'inactive' | 'opted_out';

function initialsOf(row: EdsRecipientRow): string {
  const base = ([row.first_name, row.last_name].filter(Boolean).join(' ') || row.email_address || '?').trim();
  const parts = base.split(/[\s@.]+/).filter(Boolean);
  const chars = parts.length >= 2 ? parts[0][0] + parts[1][0] : base.slice(0, 2);
  return chars.toUpperCase();
}

function fullName(row: EdsRecipientRow): string {
  return [row.first_name, row.last_name].filter(Boolean).join(' ');
}

export function MobileEdsRecipientPage({ session }: MobilePageComponentProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const { t } = useTranslation();
  const formatDate = useFormatDate();
  const [rows, setRows] = useState<EdsRecipientRow[]>([]);
  // §17.B13 relation: the recipient lists each recipient belongs to, mirroring the
  // desktop page. List names resolve through _tl (§17.B4).
  const [listsByRecipient, setListsByRecipient] = useState<Map<string, { list_id: string; name: string }[]>>(new Map());
  const [filter, setFilter] = useState('');
  const [statusFacet, setStatusFacet] = useState<StatusFacet>('active');
  const [selected, setSelected] = useState<EdsRecipientRow | null>(null);
  const [editing, setEditing] = useState<EdsRecipientRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data, error: e } = await apiDb
        .from('eds_recipient')
        .select('*')
        .order('email_address');
      if (e) throw e;
      setRows((data ?? []) as EdsRecipientRow[]);

      const [{ data: listRows }, { data: memRows }] = await Promise.all([
        apiDb.from('eds_recipient_list').select('list_id, internal_code, eds_recipient_list_tl!left(list_name)'),
        apiDb.from('eds_list_recipient').select('recipient_id, list_id'),
      ]);
      const listName = new Map<string, string>();
      for (const l of (listRows ?? []) as { list_id: string; internal_code: string; eds_recipient_list_tl?: { list_name: string }[] }[]) {
        listName.set(l.list_id, l.eds_recipient_list_tl?.[0]?.list_name ?? l.internal_code);
      }
      const byRecipient = new Map<string, { list_id: string; name: string }[]>();
      for (const mem of (memRows ?? []) as { recipient_id: string; list_id: string }[]) {
        const arr = byRecipient.get(mem.recipient_id) ?? [];
        arr.push({ list_id: mem.list_id, name: listName.get(mem.list_id) ?? mem.list_id });
        byRecipient.set(mem.recipient_id, arr);
      }
      setListsByRecipient(byRecipient);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { void reload(); }, []);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFacet === 'active' && r.is_active !== 'Y') return false;
      if (statusFacet === 'inactive' && r.is_active !== 'N') return false;
      if (statusFacet === 'opted_out' && r.is_globally_opted_out !== 'Y') return false;
      if (!q) return true;
      return (
        r.email_address.toLowerCase().includes(q) ||
        (r.first_name ?? '').toLowerCase().includes(q) ||
        (r.last_name ?? '').toLowerCase().includes(q)
      );
    });
  }, [rows, filter, statusFacet]);

  const facets: { key: StatusFacet; label: string }[] = [
    { key: 'all', label: t('mobile.eds_recipients.all', 'All') },
    { key: 'active', label: t('common.boolean.active', 'Active') },
    { key: 'inactive', label: t('common.boolean.inactive', 'Inactive') },
    { key: 'opted_out', label: t('mobile.eds_recipients.opted_out', 'Opted out') },
  ];

  // The list-row pill surfaces the most important standing: opted-out (blocks all
  // sends) takes priority over the active/inactive flag.
  const rowPill = (r: EdsRecipientRow) => {
    if (r.is_globally_opted_out === 'Y') {
      return <MobileStatusPill tone="no">{t('mobile.eds_recipients.opted_out', 'Opted out')}</MobileStatusPill>;
    }
    return (
      <MobileStatusPill tone={r.is_active === 'Y' ? 'ok' : 'no'}>
        {r.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
      </MobileStatusPill>
    );
  };

  const leadTone = (r: EdsRecipientRow): MobileLeadTone =>
    r.is_globally_opted_out === 'Y' ? 'red' : r.is_active === 'Y' ? 'green' : 'slate';

  const toggleOptOut = async (r: EdsRecipientRow) => {
    setActionBusy(true);
    setError(null);
    const next: YN = r.is_globally_opted_out === 'Y' ? 'N' : 'Y';
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = await routedUpdate(session, 'eds_recipient', 'recipient_id', r.recipient_id, {
      is_globally_opted_out: next,
    });
    setActionBusy(false);
    if (e) { setError(e.message); return; }
    setSelected(null);
    void reload();
  };

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.eds_recipients.title', 'Recipients')}
        description={t('mobile.eds_recipients.description', 'People who can receive email campaigns, and their consent standing.')}
      />

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.eds_recipients.search', 'Search recipients')}
          aria-label={t('mobile.eds_recipients.search', 'Search recipients')}
        />
      </div>

      <MobileFilterChips
        chips={facets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => statusFacet === v}
        onSelect={(v) => setStatusFacet(v as StatusFacet)}
      />

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.eds_recipients.none', 'No recipients match')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((r) => (
            <MobileCardRow
              key={r.recipient_id}
              lead={r.is_globally_opted_out === 'Y' ? <MailX /> : <Mail />}
              leadTone={leadTone(r)}
              title={fullName(r) || r.email_address}
              subtitle={r.email_address}
              trailing={rowPill(r)}
              chevron
              onClick={() => setSelected(r)}
            />
          ))}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => { if (!actionBusy) setSelected(null); }} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.eds_recipients.detail', 'Recipient detail')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av">{initialsOf(selected)}</span>
              <span>
                <div className="m-nm">{fullName(selected) || selected.email_address}</div>
                <div className="m-em">{selected.email_address}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow
                label={t('mobile.eds_recipients.opt_out', 'Marketing consent')}
                value={
                  <MobileStatusPill tone={selected.is_globally_opted_out === 'Y' ? 'no' : 'ok'}>
                    {selected.is_globally_opted_out === 'Y'
                      ? t('mobile.eds_recipients.opted_out', 'Opted out')
                      : t('mobile.eds_recipients.subscribed', 'Subscribed')}
                  </MobileStatusPill>
                }
              />
              <MobileDetailRow
                label={t('mobile.eds_recipients.status', 'Status')}
                value={
                  <MobileStatusPill tone={selected.is_active === 'Y' ? 'ok' : 'no'}>
                    {selected.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                  </MobileStatusPill>
                }
              />
              <MobileDetailRow
                label={t('mobile.eds_recipients.language', 'Preferred language')}
                value={selected.preferred_language_id ? localeName(selected.preferred_language_id) : t('common.none', 'None')}
              />
              <MobileDetailRow label={t('mobile.eds_recipients.added', 'Added')} value={formatDate(selected.created_at)} />
            </MobileContentCard>
            {/* §17.B13 — the row's related records, stacked one group per block. */}
            <MobileContentCard>
              <MobileRelatedChildren
                session={session}
                groups={[
                  {
                    key: 'lists',
                    table: 'eds_recipient_list', pkColumn: 'list_id',
                    title: t('mobile.eds_recipients.related.lists', 'Member of lists'),
                    items: (listsByRecipient.get(selected.recipient_id) ?? [])
                      .map(l => ({ id: l.list_id, label: l.name })),
                  },
                ]}
              />
            </MobileContentCard>
            <div className="m-actionbar">
              {canChange && (<button
                type="button"
                className="m-btn m-btn-primary"
                disabled={actionBusy}
                onClick={() => { setEditing(selected); setSelected(null); }}
              >
                <SquarePen size={16} />
                {t('mobile.eds_recipients.edit', 'Edit')}
              </button>)}
              {canChange && (<button
                type="button"
                className={'m-btn ' + (selected.is_globally_opted_out === 'Y' ? 'm-btn-ghost' : 'm-btn-danger')}
                disabled={actionBusy}
                onClick={() => void toggleOptOut(selected)}
              >
                {selected.is_globally_opted_out === 'Y' ? <MailCheck size={16} /> : <MailX size={16} />}
                {selected.is_globally_opted_out === 'Y'
                  ? t('mobile.eds_recipients.reinstate', 'Reinstate')
                  : t('mobile.eds_recipients.opt_out_action', 'Opt out')}
              </button>)}
              <button type="button" className="m-btn m-btn-ghost" disabled={actionBusy} onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>

      <RecipientEditSheet
        session={session}
        recipient={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); void reload(); }}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Edit sheet — touch-first counterpart of the desktop RecipientModal.
// Edits the scalar fields (names, opt-out, active) and saves through the Common
// Routine (routedUpdate) — §2. Email is immutable, mirroring the desktop rule.
// ---------------------------------------------------------------------------

interface RecipientEditSheetProps {
  session: AuthSession;
  recipient: EdsRecipientRow | null;
  onClose: () => void;
  onSaved: () => void;
}

function RecipientEditSheet({ session, recipient, onClose, onSaved }: RecipientEditSheetProps) {
  const { t } = useTranslation();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [optedOut, setOptedOut] = useState(false);
  const [active, setActive] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Seed the form whenever a different recipient is opened.
  useEffect(() => {
    if (!recipient) return;
    setFirstName(recipient.first_name ?? '');
    setLastName(recipient.last_name ?? '');
    setOptedOut(recipient.is_globally_opted_out === 'Y');
    setActive(recipient.is_active === 'Y');
    setError(null);
  }, [recipient]);

  const save = async () => {
    if (!recipient) return;
    setBusy(true);
    setError(null);
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = await routedUpdate(session, 'eds_recipient', 'recipient_id', recipient.recipient_id, {
      first_name: firstName.trim() || null,
      last_name: lastName.trim() || null,
      is_globally_opted_out: optedOut ? 'Y' : 'N',
      is_active: active ? 'Y' : 'N',
    });
    setBusy(false);
    if (e) { setError(e.message); return; }
    onSaved();
  };

  const open = recipient !== null;

  return (
    <>
      <div className={'m-scrim' + (open ? ' m-open' : '')} onClick={() => { if (!busy) onClose(); }} />
      <div className={'m-sheet' + (open ? ' m-open' : '')} aria-label={t('mobile.eds_recipients.edit', 'Edit recipient')}>
        <div className="m-grabber" />
        {recipient && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><Mail size={20} /></span>
              <span>
                <div className="m-nm">{t('mobile.eds_recipients.edit', 'Edit recipient')}</div>
                <div className="m-em">{recipient.email_address}</div>
              </span>
            </div>

            <div className="m-fieldcard">
              <div className="m-formrow">
                <label htmlFor="m-eds-first">{t('mobile.eds_recipients.first_name', 'First name')}</label>
                <input
                  id="m-eds-first"
                  className="m-inp"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="m-formrow">
                <label htmlFor="m-eds-last">{t('mobile.eds_recipients.last_name', 'Last name')}</label>
                <input
                  id="m-eds-last"
                  className="m-inp"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>
              <div className="m-formrow m-formrow-inline">
                <span>
                  <div className="m-formrow-l">{t('mobile.eds_recipients.opted_out', 'Opted out')}</div>
                  <div className="m-formrow-s">{t('mobile.eds_recipients.opted_out_hint', 'Blocks all future sends')}</div>
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={optedOut}
                  className={'m-switch ' + (optedOut ? 'm-on' : 'm-off')}
                  onClick={() => setOptedOut((v) => !v)}
                >
                  <span className="m-knob" />
                </button>
              </div>
              <div className="m-formrow m-formrow-inline">
                <span>
                  <div className="m-formrow-l">{t('common.boolean.active', 'Active')}</div>
                  <div className="m-formrow-s">{t('mobile.eds_recipients.active_hint', 'Recipient can receive sends')}</div>
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={active}
                  className={'m-switch ' + (active ? 'm-on' : 'm-off')}
                  onClick={() => setActive((v) => !v)}
                >
                  <span className="m-knob" />
                </button>
              </div>
            </div>

            {error && <div className="m-card"><div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div></div>}

            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-primary" disabled={busy} onClick={() => void save()}>
                {busy ? t('common.saving', 'Saving…') : t('common.save', 'Save changes')}
              </button>
              <button type="button" className="m-btn m-btn-ghost" disabled={busy} onClick={onClose} aria-label={t('common.cancel', 'Cancel')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
